import React, { useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import * as Recharts from 'recharts';

import ChartCard from '../../ChartComponents/ChartCard';
import Info from '../../../../components/Info';
import {
  GENDER_FEMALE_COLOR,
  GENDER_MALE_COLOR,
  compareAgeBuckets,
  getAgeBucketColor,
} from '../constants';
import style from './style.module.scss';
import chartStyle from '../style.module.scss';

const RADIAN = Math.PI / 180;

const avg = (values) => {
  if (!values.length) return 0;
  return Math.round(values.reduce((sum, n) => sum + (Number(n) || 0), 0) / values.length);
};

const buildCrowdCountAverages = (data) => {
  const genderTimeline = data?.genderTimeline || [];
  const males = genderTimeline.map(({ male }) => male);
  const females = genderTimeline.map(({ female }) => female);
  // Age buckets omit zero intervals; divide by total scan intervals (same as gender).
  const intervalCount = genderTimeline.length || (data?.countTimeline || []).length;

  const ageTimeline = data?.ageTimeline || {};
  const ageBuckets = Object.keys(ageTimeline)
    .sort(compareAgeBuckets)
    .map((bucket, index) => {
      const sum = (ageTimeline[bucket] || []).reduce(
        (total, { y }) => total + (Number(y) || 0),
        0,
      );
      return {
        name: bucket,
        value: intervalCount ? Math.round(sum / intervalCount) : 0,
        fill: getAgeBucketColor(bucket, index),
      };
    })
    .filter(({ value }) => value > 0);

  const male = avg(males);
  const female = avg(females);

  return {
    gender: [
      { name: 'Male', value: male, fill: GENDER_MALE_COLOR },
      { name: 'Female', value: female, fill: GENDER_FEMALE_COLOR },
    ].filter(({ value }) => value > 0),
    ageBuckets,
  };
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0];
  const {
    value,
    name: itemName,
    fill,
    color,
  } = item;
  const name = itemName || item.payload?.name;
  const sliceColor = item.payload?.fill || fill || color;

  return (
    <div className={style.tooltip}>
      <span className={style.tooltipDot} style={{ backgroundColor: sliceColor }} />
      <span className={style.tooltipText}>{`${name}: ${value}`}</span>
    </div>
  );
};

const renderSliceLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  name,
  value,
}) => {
  if (!percent || percent < 0.03) {
    return null;
  }

  const radius = outerRadius + 22;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#4E5155"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={500}
    >
      {`${name} ${value} (${Math.round(percent * 100)}%)`}
    </text>
  );
};

const AveragePie = ({ title, content, slices }) => {
  if (!slices.length) {
    return null;
  }

  return (
    <ChartCard
      title={(
        <Info label={title} content={content} className="ml-2" />
      )}
    >
      <div className={chartStyle.chartFix}>
        <Recharts.ResponsiveContainer width="100%" height={360}>
          <Recharts.PieChart margin={{
            top: 16, right: 24, bottom: 8, left: 24,
          }}
          >
            <Recharts.Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={85}
              paddingAngle={2}
              isAnimationActive={false}
              label={renderSliceLabel}
              labelLine={{
                stroke: '#B7B8BA',
                strokeWidth: 1,
              }}
            >
              {slices.map((slice) => (
                <Recharts.Cell key={slice.name} fill={slice.fill} />
              ))}
            </Recharts.Pie>
            <Recharts.Tooltip content={<PieTooltip />} />
            <Recharts.Legend
              iconType="circle"
              iconSize={10}
              wrapperStyle={{ fontSize: '13px', color: '#4E5155' }}
            />
          </Recharts.PieChart>
        </Recharts.ResponsiveContainer>
      </div>
    </ChartCard>
  );
};

const AveragesStrip = ({ data }) => {
  const averages = useMemo(() => buildCrowdCountAverages(data), [data]);

  if (!averages.gender.length && !averages.ageBuckets.length) {
    return null;
  }

  return (
    <div className={style.strip}>
      <Row className="g-3">
        <Col xs={12} md={6}>
          <AveragePie
            title="Average age"
            content="Share of average face counts by age bucket across scans"
            slices={averages.ageBuckets}
          />
        </Col>
        <Col xs={12} md={6}>
          <AveragePie
            title="Average gender"
            content="Share of average male vs female face counts across scans"
            slices={averages.gender}
          />
        </Col>
      </Row>
    </div>
  );
};

export default AveragesStrip;
